import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { Optional, Resume } from 'packages/types'
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import {
  SectionNames,
  selectors as editorSelectors,
  actions as editorActions,
  ResumeSectionId,
  UpdateSimpleFieldFunc,
  MoveSectionPayload,
  AddCustomSectionPayload,
} from 'builder/modules/resumeEditor'
import { actions as critiqueActions } from 'builder/modules/resumeCritiqueReview'
import { actions as coverLetterActions } from 'builder/modules/coverLetterEditor'
import { actions as uiActions } from 'builder/modules/ui'
import { AdditionalSections } from 'builder/components/AdditionalSections'
import { generateRandomId } from 'builder/utils/generateRandomId'
import { camelToSnake } from 'builder/utils/camelToSnake'
import { useConfig } from 'builder/hooks/useConfig'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { GeneralEditorLevels } from 'builder/modules/generalEditor'
import { selectors as userSelectors, UserType } from 'builder/modules/user'
import {
  selectors as resumeReviewSelectors,
  actions as resumeReviewActions,
  ResumeCritiqueStatuses,
} from 'builder/modules/resumeReview'
import { IFRAME_PARAMS } from 'builder/modules/resumeCritiqueReview/constants'
import { useI18n } from 'builder/hooks/useI18n'
import ProfileSection from '../ProfileSummary/ProfileSection'
import GenerateAICVBannerNew from '../GenerateAICVBannerNew'
import GenerateAICVBanner from '../GenerateAICVBanner'
import { displaySectionPerTemplateConfig } from '../DocumentEditor/ATSResumeEditor/builderSectionsConfig'
import PowerStatementSection from '../PowerStatement/PowerStatementSection'
import {
  AddCardFunc,
  CardOperationFunc,
  MoveCardFunc,
  MoveSubItemFunc,
  UpdateCardFunc,
} from '../Card'
import { DeleteSectionFunc, RenameSectionFunc } from '../Section/types'
import CritiqueReviewBanner from '../CritiqueReviewBanner'
import CritiqueFreeUserModal from '../CritiqueFreeUserModal/CritiqueFreeUserModal'
import { useTourConfig } from '../DocumentEditor/DocumentTour/hooks/useTourConfig'
import {
  AcvBannerContainer,
  Banners,
  RecommendedSteps,
  RecommendedStepsContainer,
  SectionsListContainer,
  SectionsListFooter,
} from './styles'
import { groupSectionsBy, constructSectionsData } from './constructSectionsData'
import { SortableSection } from './SortableSection'

type Props = {
  resume: Resume
  updateCard: UpdateCardFunc
  deleteCard: CardOperationFunc
  addCard: (cardId: string, sectionId: ResumeSectionId, options?: { shouldOpen: boolean }) => void
  addCustomSection: (customSectionId: string, options: AddCustomSectionPayload['options']) => void
  moveCard: MoveCardFunc
  moveSection: (payload: MoveSectionPayload) => void
  levels: GeneralEditorLevels
  updateSimpleField: UpdateSimpleFieldFunc
  renameSection: RenameSectionFunc
  deleteSection: DeleteSectionFunc
  appLocale?: string
  addCardSubItem?: CardOperationFunc
  moveCardSubItem?: MoveSubItemFunc
}

export const NewSectionsList = (props: Props) => {
  const {
    resume,
    levels,
    addCustomSection,
    renameSection,
    deleteSection,
    moveCard,
    deleteCard,
    updateCard,
    updateSimpleField,
    moveSection,
    addCard,
    addCardSubItem,
    moveCardSubItem,
  } = props
  const {
    hideSkillLevel,
    locale,
    template,
    skills,
    position,
    firstName,
    lastName,
    email,
    workExperiences,
    sectionsOrder,
  } = resume
  const config = useConfig()
  const dispatch = useDispatch()
  const { onUpdateDragSections } = useTourConfig()
  const openedCard = useTypedSelector(editorSelectors.openedCard)
  const openedSections = useTypedSelector(editorSelectors.openedSections)
  const aiClFreeAttempts = useTypedSelector(state => state.user.data?.aiClFreeAttempts) || 0
  const isAPremiumUser = useTypedSelector(state => state.user.data?.hasPremiumFeatures)
  const currentUserDocuments = useTypedSelector(state => state.panel.documents)
  const autoTailoringFromPublicJobBoard = useTypedSelector(
    editorSelectors.autoTailoringFromPublicJobBoard,
  )
  const userData = useTypedSelector(userSelectors.userData)
  const userType = userData?.billingInfo.userType
  const isFreeUser = userType === UserType.free

  const critique = useTypedSelector(state => resumeReviewSelectors.critique(state, resume.id))
  const critiqueReviewStatus = critique?.status
  const isCritiqueReviewStatusPending = critiqueReviewStatus === ResumeCritiqueStatuses.pending
  const isCritiqueReviewStatusFinished = critiqueReviewStatus === ResumeCritiqueStatuses.finished
  const isCritiqueReviewStatusFailed = critiqueReviewStatus === ResumeCritiqueStatuses.failed
  const userHasAtLeastOneCritique = useTypedSelector(
    resumeReviewSelectors.userHasAtLeastOneCritique,
  )
  const [showCritiqueFreeUserModal, setShowCritiqueFreeUserModal] = useState(false)
  const critiqueEntryPoints = config?.features.critiqueEntryPoints

  const { i18n } = useI18n()

  useEffect(() => {
    dispatch(resumeReviewActions.fetchCritiques())
  }, [dispatch])

  const handleCardAdd: AddCardFunc = useCallback(
    (sectionId, options) => {
      const cardId = generateRandomId()
      addCard(cardId, sectionId, { ...(options || {}), shouldOpen: true })
    },
    [addCard],
  )

  const handleGenerateClick = useCallback(() => {
    dispatch(editorActions.setIsClickedOnAiCVBanner(true))
  }, [dispatch])

  const isCardOpen = useCallback(
    (cardId: Optional<number | string>, sectionId: ResumeSectionId) => {
      if (!openedCard) return false

      return openedCard.id === cardId && openedCard.sectionId === sectionId
    },
    [openedCard],
  )

  const handleCardToggle = useCallback(
    (cardId: Optional<number | string>, sectionId: ResumeSectionId) => {
      const isOpen = isCardOpen(cardId, sectionId)

      dispatch(editorActions.openCard(isOpen ? null : { id: cardId, sectionId }))
    },
    [dispatch, isCardOpen],
  )

  const handleSectionToggle = useCallback(
    (sectionId: ResumeSectionId) => {
      const isOpened = openedSections[sectionId] ?? true
      dispatch(editorActions.setOpenedSections({ [sectionId]: !isOpened }))
    },
    [dispatch, openedSections],
  )

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) return

      const draggableId = active.id as ResumeSectionId
      const oldIndex = sectionsOrder.indexOf(active.id as string)
      const newIndex = sectionsOrder.indexOf(over.id as string)

      moveSection({
        offset: newIndex - oldIndex,
        sectionId: draggableId,
      })

      onUpdateDragSections()
      trackInternalEvent('change_sections_order', {
        section: typeof draggableId === 'string' ? camelToSnake(draggableId) : draggableId,
        old_position: oldIndex + 1,
        new_position: newIndex + 1,
      })
    },
    [moveSection, onUpdateDragSections, sectionsOrder],
  )

  const showGenerateAICVButton = useMemo(() => {
    if (!config?.features.aiCoverLetters) {
      return false
    }
    if (!firstName || !lastName || !email) {
      return false
    }

    const hasWorkExperience =
      workExperiences.findIndex(
        ({ title, employer, dateFrom }) => title && employer && dateFrom,
      ) !== -1

    return hasWorkExperience
  }, [config?.features.aiCoverLetters, firstName, lastName, email, workExperiences])

  const sections = constructSectionsData(resume)
  const filteredSections = sections.filter(section =>
    displaySectionPerTemplateConfig(resume.template, section.id),
  )
  const groups = groupSectionsBy(filteredSections, 'sortable')
  const showAIUsagePaywall = !isAPremiumUser && aiClFreeAttempts <= 0
  const currentUserCoverLetters = currentUserDocuments.filter(
    document => document.type === 'cover_letter',
  )
  const hasCoverLettersLimitReached = !isAPremiumUser && currentUserCoverLetters.length >= 1
  const showPowerStatementSection = template === 'shanghai'

  const generateCoverLetterHandler = useCallback(() => {
    if (hasCoverLettersLimitReached) {
      dispatch(uiActions.openLimitModal('cover_letter'))
    } else if (showAIUsagePaywall) {
      dispatch(coverLetterActions.setShowAICoverLetterPaywall())
    } else {
      handleGenerateClick()
    }
  }, [handleGenerateClick, dispatch, hasCoverLettersLimitReached, showAIUsagePaywall])

  const sendResumeToCritiqueReview = () => {
    if (isFreeUser) {
      setShowCritiqueFreeUserModal(true)
      return
    }

    trackInternalEvent('click_send_resume_review', { where: 'builder' })

    dispatch(
      resumeReviewActions.sendResumeToCritique({
        resumeId: resume.id,
      }),
    )
  }

  const viewResumeCritiqueReview = (url?: string) => {
    if (url) {
      trackInternalEvent('click_read_resume_critique_banner', { where: 'builder' })
      dispatch(critiqueActions.setCritiquePageVisitedCount(true))
      window.open(`${url}${IFRAME_PARAMS}`)
    }
  }

  const reviewStatus = isCritiqueReviewStatusFinished
    ? ResumeCritiqueStatuses.finished
    : isCritiqueReviewStatusFailed
    ? ResumeCritiqueStatuses.failed
    : isCritiqueReviewStatusPending
    ? ResumeCritiqueStatuses.pending
    : undefined

  return (
    <SectionsListContainer>
      {showPowerStatementSection && (
        <PowerStatementSection {...{ sectionId: SectionNames.powerStatement }} />
      )}
      <ProfileSection {...{ sectionId: SectionNames.profile }} />
      {groups.map((group, groupIndex) => {
        const content = group.sections.map((section, sectionIndex) => (
          <SortableSection
            key={section.id}
            index={sectionIndex}
            isOpened={openedSections[section.id] ?? true}
            item={section}
            levels={levels}
            skills={skills}
            position={position}
            hideSkillLevel={hideSkillLevel}
            locale={locale}
            onRename={renameSection}
            onDelete={deleteSection}
            onToggle={handleSectionToggle}
            onAdd={handleCardAdd}
            isCardOpen={isCardOpen}
            onCardChange={updateCard}
            onCardDelete={deleteCard}
            onCardMove={moveCard}
            onCardToggle={handleCardToggle}
            template={resume.template}
            updateSimpleField={updateSimpleField}
            onAddCardSubItem={addCardSubItem}
            onSubItemMove={moveCardSubItem}
          />
        ))

        if (!group.sortable) return <div key={groupIndex}>{content}</div>

        return (
          <DndContext key={groupIndex} onDragEnd={onDragEnd} collisionDetection={closestCenter}>
            <SortableContext items={sectionsOrder} strategy={verticalListSortingStrategy}>
              {content}
            </SortableContext>
          </DndContext>
        )
      })}

      <SectionsListFooter>
        <AcvBannerContainer>
          {showGenerateAICVButton && (
            <RecommendedStepsContainer>
              {critiqueEntryPoints && (
                <RecommendedSteps>
                  {i18n.t('builder.sections_list.recommended_steps')}
                </RecommendedSteps>
              )}
              <Banners>
                {!autoTailoringFromPublicJobBoard.enabled &&
                  (config?.features.resumeOptimizer ? (
                    <GenerateAICVBannerNew
                      onClickGenerate={generateCoverLetterHandler}
                      hideHeading={critiqueEntryPoints}
                    />
                  ) : (
                    <GenerateAICVBanner onClickGenerate={generateCoverLetterHandler} />
                  ))}
                {(!userHasAtLeastOneCritique || reviewStatus) && critiqueEntryPoints && (
                  <CritiqueReviewBanner
                    sendResumeToReview={sendResumeToCritiqueReview}
                    critiqueReviewStatus={reviewStatus}
                    viewResumeCritiqueReview={() =>
                      viewResumeCritiqueReview(critique?.iframeUrl || '')
                    }
                  />
                )}
              </Banners>
            </RecommendedStepsContainer>
          )}
        </AcvBannerContainer>
        <AdditionalSections
          sections={filteredSections}
          onAdd={sectionId =>
            handleCardAdd(sectionId, { scrollIntoViewport: true, shouldOpen: true })
          }
          onAddCustom={externalId => addCustomSection(externalId, { scrollIntoViewport: true })}
        />
      </SectionsListFooter>

      {showCritiqueFreeUserModal && (
        <CritiqueFreeUserModal
          onClose={() => setShowCritiqueFreeUserModal(false)}
          redirectUrl={`/app/resumes/${resume.id}/edit`}
          resumeIdToSendToCritique={resume.id}
        />
      )}
    </SectionsListContainer>
  )
}
